// src/js/main.js
//
// アプリのエントリポイント。IMPLEMENTATION.md 5.1「フレームワークを使わない」方針のため、
// ここが唯一の起動シーケンスであり、以後の描画はすべて Web Components + router.js に委ねる。
//
// 00_情報設計_全体アーキテクチャ.md 原則2「サーバー完結の原則」を守るため、
// 初回表示のDOMはサーバーHTMLをそのまま使い、このファイルは再描画を行わない。
// 行うのは (1) 基盤コンポーネントの登録、(2) config取得とストアへの投入、
// (3) ルーターの初期化、(4) 現在ページに対応するルートモジュールの動的import・
// 漸進的強化、(5) settings_version の差分検知ポーリング、(6) エラー捕捉の土台。
import './core/icon.js'; // <xte-icon> の customElements 登録（別担当、副作用import）
// <xte-illust> の customElements 登録。空状態イラストは parts/empty-state.php が
// 全ルートで出力するうえ、xte-notification-list.js と xte-playlist-editor.js が
// document.createElement('xte-illust') で動的生成する。特定ルートに属さないため
// icon.js と同じくここで登録する。
// この import が欠けていた間、illustration.js はどのエントリからも到達できない
// 孤立モジュールとなり、customElements.define('xte-illust') が実行されず、
// 空状態のイラストが全画面で表示されなかった。
import './core/illustration.js';
// <xte-bottom-nav> の customElements 登録。ボトムナビは全画面の永続シェル層に
// 常時表示される（parts/app-shell-close.php の do_action('xte/render/bottom_nav')）ため、
// 動的importではなく初期バンドルに含める（IMPLEMENTATION.md 5.2節。
// docs/HANDOFF.md「ボトムナビ担当 → ビルド設定/フロントエンド基盤担当」参照）。
import './components/xte-bottom-nav.js';
import { createStore } from './core/store.js';
import { initRouter, matchRoute, loadRouteModule } from './core/router.js';
import { api, bootstrapApi, getCachedSettingsVersion, forceRefreshConfig } from './core/api.js';
import { refreshCopyStore } from './core/copy.js';

// 34_WPプラグイン_アーキテクチャと管理画面.md の決定: settings_version の
// 差分検知ポーリングは60秒間隔（00文書 4.4節と対応）。
const SETTINGS_POLL_INTERVAL_MS = 60000;
const RUM_ERROR_BUFFER_LIMIT = 20;

/* ------------------------------------------------------------------ *
 * ストア初期化: 00文書 5.1節の状態一覧を、キーごとの永続化先を宣言して構築する。
 * ------------------------------------------------------------------ */
export const store = createStore(
  {
    config: null, // GET /config のレスポンス（rest_nonce, settings_version 等）
    player: {
      currentTime: 0,
      isPlaying: false,
      currentVideoId: null,
    },
    ui: {
      soundMuted: true, // 自動再生ポリシー対応のため既定はミュート（00文書 5.1節）
      gridDensity: 'standard',
      lastSort: 'hotness',
    },
    feed: {
      position: 0,
      shuffleSeed: null,
    },
    rum: {
      errors: [],
    },
  },
  {
    persistence: {
      // 00文書 5.1節の表に対応: 音声ON/OFF・グリッド密度・直近の並べ替えはlocalStorage。
      // 再生位置・いいね・ブックマーク・視聴履歴はIndexedDB永続化だが、これらは
      // 対象動画IDに紐づく粒度で扱う必要があるため、各コンポーネント側が
      // core/db.js の createDbBackedRecord() を用いて個別に永続化する
      // （store.js はキー単位の永続化先切替の「仕組み」だけを提供する）。
      'ui.soundMuted': { type: 'localStorage', key: 'xte:sound-muted' },
      'ui.gridDensity': { type: 'localStorage', key: 'xte:grid-density' },
      'ui.lastSort': { type: 'localStorage', key: 'xte:last-sort' },
    },
  }
);

/* ------------------------------------------------------------------ *
 * エラー捕捉の土台: 握りつぶさず、必ず記録してからRUMへ送る経路を用意する。
 * 実際の送信バッチ処理は重量級のため、必要になった時点で動的importする
 * （43_パフォーマンス_CoreWebVitals.md 4.5節「軽い処理はメインスレッドのままでよい」の逆で、
 * RUM送出自体はユーザー体験に寄与しない処理なので遅延実行してよい対象）。
 * ------------------------------------------------------------------ */
function reportError(kind, error, extra = {}) {
  const normalized = error instanceof Error ? error : new Error(String(error));
  const entry = {
    kind,
    message: normalized.message,
    stack: normalized.stack ?? null,
    url: window.location.href,
    at: new Date().toISOString(),
    ...extra,
  };

  // eslint等を導入していない構成のため、開発時に気付けるよう必ずconsoleへ出す
  // （IMPLEMENTATION.md「エラーの握りつぶしをしない」）。
  console.error(`[xte:${kind}]`, normalized, extra);

  const buffer = store.get('rum.errors') ?? [];
  store.set('rum.errors', [...buffer, entry].slice(-RUM_ERROR_BUFFER_LIMIT));

  document.dispatchEvent(new CustomEvent('xte:client-error', { detail: entry }));
}

window.addEventListener('error', (event) => {
  reportError('window-error', event.error ?? new Error(event.message), {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  reportError('unhandled-rejection', reason);
});

/* ------------------------------------------------------------------ *
 * ルートモジュールのマウント。router.js からのクライアント遷移時と、
 * 初回ロード時の漸進的強化の両方から呼ばれる共通経路。
 * ------------------------------------------------------------------ */
// 直前のルートの mount() が返したクリーンアップ関数（routes/_passthrough.js に
// 明記された契約「戻り値を返す場合は unmount 時に呼ばれる」）。downloads-entry.js は
// ポーリングタイマーと visibilitychange リスナーの解放関数を返すが、これまで
// どこからも呼ばれておらず、遷移をまたいでリークしていた（HANDOFF申し送り済みの件）。
// PJAX遷移で `#xte-app` の中身が丸ごと差し替わるようになったため、
// 次の mount() の直前に必ず呼んで前ルートの後始末を完了させる。
// （カスタム要素自身の後始末は、差し替えによるDOM切断で disconnectedCallback が
// 発火する既存の仕組みが担う。ここで扱うのはエントリモジュール層の資源だけ。）
let activeRouteCleanup = null;

async function mountRoute(routeName, mod, data, url) {
  if (typeof activeRouteCleanup === 'function') {
    try {
      activeRouteCleanup();
    } catch (err) {
      // 後始末の失敗で遷移自体を壊さない（記録だけして続行する）。
      reportError('route-cleanup-failed', err, { route: routeName });
    }
  }
  activeRouteCleanup = null;

  const container = document.getElementById('xte-app') ?? document.body;
  if (!mod || typeof mod.mount !== 'function') {
    reportError('route-module-invalid', new Error(`ルート "${routeName}" のモジュールに mount() がありません。`));
    return;
  }
  const result = await mod.mount(container, data, { url, store, api });
  if (typeof result === 'function') {
    activeRouteCleanup = result;
  }
}

/**
 * サーバーHTMLに埋め込まれた初期データを読む。00文書 4.2節の方針により、
 * 初回表示分と同一内容をクライアントが再度RESTで取得することはしない。
 * PHPテンプレート側は `<script type="application/json" id="xte-initial-data">` に
 * 現在ページのデータをJSONで出力する契約とする（docs/HANDOFF.md参照）。
 */
function readEmbeddedInitialData() {
  const el = document.getElementById('xte-initial-data');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent ?? 'null');
  } catch (err) {
    reportError('initial-data-parse-failed', err);
    return null;
  }
}

/**
 * 現在表示中のサーバーHTMLに対応するルートモジュールを動的importし、
 * 漸進的強化（イベントバインド・ジェスチャ初期化等）を行う。
 * 失敗してもサーバーHTML自体は既に表示済みなので、閲覧機能そのものは失われない
 * （00文書 原則3）。
 */
async function hydrateCurrentRoute() {
  const url = new URL(window.location.href);
  const route = matchRoute(url);
  if (!route) return; // 404等、対応外ルートはサーバーHTMLのみで完結させる

  try {
    const mod = await loadRouteModule(route.name);
    const data = readEmbeddedInitialData();
    await mountRoute(route.name, mod, data, url);
  } catch (err) {
    reportError('hydrate-failed', err, { route: route.name });
  }
}

/* ------------------------------------------------------------------ *
 * config取得 + settings_version の差分検知ポーリング（00文書 4.4節・34文書の決定）。
 * ------------------------------------------------------------------ */
async function bootstrapConfig() {
  try {
    const config = await bootstrapApi();
    if (config) store.set('config', config);
  } catch (err) {
    reportError('config-bootstrap-failed', err);
  }
}

function startSettingsVersionPolling() {
  setInterval(async () => {
    try {
      const before = getCachedSettingsVersion();
      const config = await forceRefreshConfig();
      if (!config) return;

      store.set('config', config);

      if (before !== null && config.settings_version !== before) {
        // 00文書 4.4節: ページ全体のリロードは要求せず、影響を受ける機能だけを
        // 再初期化する。文言（44文書25.3節）はここで先回りして再取得しておく。
        await refreshCopyStore().catch(() => {});
        document.dispatchEvent(
          new CustomEvent('xte:settings-changed', {
            detail: { settingsVersion: config.settings_version },
          })
        );
      }
    } catch (err) {
      reportError('settings-poll-failed', err);
    }
  }, SETTINGS_POLL_INTERVAL_MS);
}

/* ------------------------------------------------------------------ * 起動シーケンス * ------------------------------------------------------------------ */
async function boot() {
  await bootstrapConfig();
  startSettingsVersionPolling();

  // 現在のページ種別に応じて必要なモジュールだけを動的importする
  // （フィード/グリッド/シングル/検索/通知/プレイリスト/投稿。43文書 7.1節）。
  await hydrateCurrentRoute();

  // ルーターは hydrateCurrentRoute() の完了後に起動する。先に起動すると、
  // 初回ハイドレーションの mount() と、クリック由来の navigate() の mount() が
  // 同じ #xte-app に対して同時に走りうる。起動前のクリックは <a> の既定動作
  // （フルページ遷移）として正しく処理されるため、待って問題ない（00文書 原則3）。
  const router = initRouter({
    mount: mountRoute,
    onError: (err, url) => reportError('navigation-failed', err, { targetUrl: url.href }),
  });

  // 20_ボトムナビゲーション.md 8.1節・9.3節が `window.xteRouter` を前提に
  // 書かれているため、返り値を破棄せず公開する。他コンポーネントから
  // 「リンククリック以外の経路」でクライアント遷移させたい場合の唯一の入口。
  window.xteRouter = router;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    boot().catch((err) => reportError('boot-failed', err));
  }, { once: true });
} else {
  boot().catch((err) => reportError('boot-failed', err));
}
